import React, { useState, useCallback, useRef } from 'react';
import {
  Avatar,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import Autosuggest from 'react-autosuggest';
import { makeStyles } from 'tss-react/mui';
import { AdminUserSearchService, AdminUserSearchResult } from 'us-web-services';

interface UserSearchProps {
  onSearch: (term: string) => void;
  onSelectUser: (user: AdminUserSearchResult) => void;
}

const useStyles = makeStyles()(() => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  suggestionsContainerOpen: {
    position: 'absolute',
    zIndex: 1300,
    left: 0,
    right: 0,
    maxHeight: 400,
    overflow: 'auto',
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 16px',
  },
  avatar: {
    width: 32,
    height: 32,
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontWeight: 500,
  },
  userEmail: {
    fontSize: '0.75rem',
    color: 'rgba(0, 0, 0, 0.6)',
  },
}));

function UserSearch({ onSearch, onSelectUser }: UserSearchProps) {
  const { classes } = useStyles();
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<AdminUserSearchResult[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const getSuggestions = useCallback(async (inputValue: string) => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    try {
      const response = await AdminUserSearchService.search({
        searchTerm: inputValue,
        limit: 10,
      });

      return response.data.data || [];
    } catch {
      return [];
    }
  }, []);

  const debouncedGetSuggestions = useCallback(
    (inputValue: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(async () => {
        const results = await getSuggestions(inputValue);

        setSuggestions(results);
      }, 300);
    },
    [getSuggestions],
  );

  const handleSuggestionsFetchRequested = ({ value: inputValue }: { value: string }) => {
    debouncedGetSuggestions(inputValue);
  };

  const handleSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const handleChange = (
    _event: React.FormEvent,
    { newValue }: { newValue: string },
  ) => {
    setValue(newValue);
  };

  const handleSuggestionSelected = (
    _event: React.FormEvent,
    { suggestion }: { suggestion: AdminUserSearchResult },
  ) => {
    setValue('');
    setSuggestions([]);
    onSelectUser(suggestion);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim()) {
      onSearch(value.trim());
      setSuggestions([]);
      inputRef.current?.blur();
    }
  };

  const renderInputComponent = (inputProps) => {
    const { inputRef: autosuggestInputRef = () => {}, ref, key, ...other } = inputProps;

    return (
      <TextField
        key={key}
        fullWidth
        variant='outlined'
        size='small'
        label='Search users by name, email, phone, or ID'
        InputProps={{
          inputRef: node => {
            ref(node);
            autosuggestInputRef(node);
            inputRef.current = node;
          },
        }}
        {...other}
      />
    );
  };

  const getDisplayName = (result: AdminUserSearchResult): string => {
    const fullName = `${result.firstName || ''} ${result.lastName || ''}`.trim();

    return fullName || result.name || 'Unknown';
  };

  const renderSuggestion = (suggestion: AdminUserSearchResult, { isHighlighted }: { isHighlighted: boolean }) => (
    <MenuItem selected={isHighlighted} component='div'>
      <div className={classes.suggestionItem}>
        <Avatar
          className={classes.avatar}
          src={suggestion.pictureUrl}
          alt={getDisplayName(suggestion)}
        >
          {(suggestion.firstName || suggestion.name || '?').charAt(0)}
        </Avatar>
        <div className={classes.userInfo}>
          <Typography variant='body2' className={classes.userName}>
            {getDisplayName(suggestion)}
          </Typography>
          <Typography className={classes.userEmail}>{suggestion.email}</Typography>
        </div>
      </div>
    </MenuItem>
  );

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    const { key, ...otherProps } = containerProps;

    return (
      <Paper key={key} {...otherProps} className={classes.suggestionsContainerOpen} elevation={3}>
        {children}
      </Paper>
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        onSuggestionSelected={handleSuggestionSelected}
        getSuggestionValue={(suggestion: AdminUserSearchResult) => suggestion.email || ''}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          value,
          onChange: handleChange,
          placeholder: 'Search users...',
        }}
        theme={{
          container: classes.container,
          suggestionsList: classes.suggestionsList,
        }}
      />
    </form>
  );
}

export default UserSearch;
